// Branch indices into levelsForScope, never draft.spine. Drop scoped keys at/below the swap.
import { PATH_SEPARATOR, effectiveAttrs, levelsForScope } from "../studioTypes";
import type { StudioDraft, StudioItem } from "../studioTypes";

/** Swapping branch levels k/k+1 invalidates in-branch keys at depth k or deeper; keys above k are kept. */
const invalidatedByBranchSwap = (
  key: string,
  scopePath: readonly string[],
  upperIndex: number,
): boolean => {
  const segments = key.split(PATH_SEPARATOR);
  return (
    segments.length >= upperIndex &&
    segments.length > scopePath.length &&
    scopePath.every((value, index) => segments[index] === value)
  );
};

const withoutInvalidated = <T>(
  map: Readonly<Record<string, T>> | undefined,
  scopePath: readonly string[],
  upperIndex: number,
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(map ?? {}).filter(
      ([key]) => !invalidatedByBranchSwap(key, scopePath, upperIndex),
    ),
  );

const branchLevelsOf = (draft: StudioDraft, scopePath: readonly string[]) =>
  levelsForScope(draft.spine, draft.scopedLevelOrder, scopePath);

export const canSwapLevelsUnder = (draft: StudioDraft, nodeId: string): boolean => {
  const scopePath = nodeId.split(PATH_SEPARATOR);
  return scopePath.length + 1 < branchLevelsOf(draft, scopePath).length;
};

/** The SKUs inside one branch; shared with the lift's re-lay so both use one membership rule. */
export const branchSkuIdsOf = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  nodeId: string,
): Set<string> => {
  const scopePath = nodeId.split(PATH_SEPARATOR);
  const branch = branchLevelsOf(draft, scopePath);
  const skuIds = new Set<string>();
  for (const item of items) {
    const attrs = effectiveAttrs(item, draft);
    const matches = scopePath.every(
      (value, index) => String(attrs[branch[index]?.attr ?? ""] ?? "") === value,
    );
    if (matches) skuIds.add(item.skuId);
  }
  return skuIds;
};

export const swapLevelsUnderScope = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  nodeId: string,
  /** Branch index of the upper swapped level; defaults to the level below the node. */
  upperLevelIndex?: number,
): StudioDraft => {
  const scopePath = nodeId.split(PATH_SEPARATOR);
  const branch = branchLevelsOf(draft, scopePath);
  const upperIndex = upperLevelIndex ?? scopePath.length;
  if (upperIndex < scopePath.length) return draft;
  const upper = branch[upperIndex];
  const lower = branch[upperIndex + 1];
  if (!upper || !lower) return draft;

  // As-built reading order per parent scope; after the swap `lower` sits at `upperIndex`.
  const upperOrderByPrefix = new Map<string, string[]>();
  const lowerOrderByPrefix = new Map<string, string[]>();
  let touched = 0;

  for (const item of items) {
    const attrs = effectiveAttrs(item, draft);
    const matches = scopePath.every(
      (value, index) => String(attrs[branch[index].attr] ?? "") === value,
    );
    if (!matches) continue;
    touched += 1;
    const prefix = branch
      .slice(0, upperIndex)
      .map((level) => String(attrs[level.attr] ?? ""))
      .join(PATH_SEPARATOR);
    const newUpper = String(attrs[lower.attr] ?? "");
    const newLower = String(attrs[upper.attr] ?? "");
    const upperSiblings = upperOrderByPrefix.get(prefix) ?? [];
    if (newUpper && !upperSiblings.includes(newUpper)) upperSiblings.push(newUpper);
    upperOrderByPrefix.set(prefix, upperSiblings);
    const lowerKey = `${prefix}${PATH_SEPARATOR}${newUpper}`;
    const lowerSiblings = lowerOrderByPrefix.get(lowerKey) ?? [];
    if (newLower && !lowerSiblings.includes(newLower)) lowerSiblings.push(newLower);
    lowerOrderByPrefix.set(lowerKey, lowerSiblings);
  }
  if (touched === 0) return draft;

  // The move: recorded against the acted-on node only, so siblings keep their order.
  const permuted = branch.slice(scopePath.length).map((level) => level.attr);
  const at = upperIndex - scopePath.length;
  [permuted[at], permuted[at + 1]] = [permuted[at + 1], permuted[at]];

  const scopedValueOrder: Record<string, readonly string[]> = withoutInvalidated(
    draft.scopedValueOrder,
    scopePath,
    upperIndex,
  );
  for (const [prefix, siblings] of upperOrderByPrefix) {
    if (siblings.length > 1) scopedValueOrder[prefix] = siblings;
  }
  for (const [prefix, siblings] of lowerOrderByPrefix) {
    if (siblings.length > 1) scopedValueOrder[prefix] = siblings;
  }

  return {
    ...draft,
    scopedLevelOrder: {
      // Deeper in-branch overrides are keyed by paths the swap just re-levelled.
      ...withoutInvalidated(draft.scopedLevelOrder, scopePath, upperIndex),
      [nodeId]: permuted,
    },
    scopedValueOrder,
    orientOverrides: withoutInvalidated(draft.orientOverrides, scopePath, upperIndex),
    bandOverrides: withoutInvalidated(draft.bandOverrides, scopePath, upperIndex),
  };
};
